import { firestoreService } from '@/utils/firestoreService'
import { collection, query, where, orderBy, limit, onSnapshot, addDoc } from 'firebase/firestore'
import { db } from '@/firebase/config'
import type { RpcCommand } from '@/types/Agent'
type Session = { tenantId?: string; yardId?: string; userId?: string }

export class AgentCommandService {
  constructor(private session: Session) {}

  private getRpcCollectionPath(agentId: string): string {
    return `tenants/${this.session.tenantId}/agents/${agentId}/rpc`
  }

  // Send a command to an agent
  async sendCommand(
    agentId: string,
    method: string,
    params?: Record<string, unknown>,
  ): Promise<string> {
    const rpcData: Omit<RpcCommand, 'id'> = {
      method,
      params: params || {},
      status: 'pending',
      createdAt: Date.now(),
    }

    const rpcCollection = collection(db, this.getRpcCollectionPath(agentId))
    const docRef = await addDoc(rpcCollection, rpcData)
    return docRef.id
  }

  // Update command status (typically called by the agent)
  async updateCommandStatus(
    agentId: string,
    rpcId: string,
    status: RpcCommand['status'],
    result?: unknown,
    error?: { message: string; stack?: string },
  ): Promise<void> {
    const updateData: Record<string, unknown> = {
      status,
      updatedAt: Date.now(),
    }

    if (status === 'running') {
      updateData.startedAt = Date.now()
    } else if (status === 'succeeded' || status === 'failed') {
      updateData.finishedAt = Date.now()
    }

    if (result !== undefined) {
      updateData.result = result
    }

    if (error) {
      updateData.error = error
    }

    await firestoreService.update(this.getRpcCollectionPath(agentId), rpcId, updateData)
  }

  // Listen to agent commands in real-time
  onAgentCommands(agentId: string, callback: (commands: RpcCommand[]) => void): () => void {
    const rpcCollection = collection(db, this.getRpcCollectionPath(agentId))
    const q = query(
      rpcCollection,
      where('status', 'in', ['pending', 'running']),
      orderBy('createdAt', 'asc'),
      limit(10),
    )

    return onSnapshot(q, (snapshot) => {
      const commands: RpcCommand[] = []
      snapshot.forEach((doc) => {
        commands.push({
          id: doc.id,
          ...doc.data(),
        } as RpcCommand)
      })
      callback(commands)
    })
  }

  // Get command history
  async getCommandHistory(agentId: string, limitCount: number = 50): Promise<RpcCommand[]> {
    const snapshot = await firestoreService.runQuery({
      collection: this.getRpcCollectionPath(agentId),
      orders: [{ field: 'createdAt', dir: 'desc' }],
      limit: limitCount,
      wheres: [],
      meta: { name: 'agentCommandHistory' },
    })

    return snapshot as RpcCommand[]
  }

  // Get a specific command
  async getCommand(agentId: string, rpcId: string): Promise<RpcCommand | null> {
    return await firestoreService.get<RpcCommand>(this.getRpcCollectionPath(agentId), rpcId)
  }

  // Cancel a pending command
  async cancelCommand(agentId: string, rpcId: string): Promise<void> {
    await this.updateCommandStatus(agentId, rpcId, 'failed', undefined, {
      message: 'Command cancelled by user',
    })
  }
}

// Factory function for creating service instances
export function useAgentCommandService(session: Session): AgentCommandService {
  return new AgentCommandService(session)
}
